import {Button} from '@wandb/components';
import React, {ReactFragment} from 'react';

import type * as RunFeed from '../../../util/runfeed';
import {COLUMN_LIMIT} from '../../../util/tablecols';
import {TableEditorColumn} from './TableEditorColumn';
import {TableEditorColumnField} from './TableEditorColumnField';
import {TableEditorColumnSection} from './TableEditorColumnSection';
import {useTableEditorDragDropController} from './useTableEditorDragDropController';

const MAX_ITEMS_PER_SECTION = 100;

type TableEditorColumnsProps = {
  config: RunFeed.Config;
  fixedColumns: string[];
  hiddenColumns: string[];
  highlightColumn: (column: string) => ReactFragment;
  isColumnLimitReached: boolean;
  pinnedColumns: string[];
  update(config: RunFeed.Config): void;
  visibleColumns: string[];
};

export function TableEditorColumns({
  config,
  fixedColumns,
  hiddenColumns,
  highlightColumn,
  isColumnLimitReached,
  pinnedColumns,
  update,
  visibleColumns,
}: TableEditorColumnsProps) {
  const {
    draggingColumnName,
    onDragStart,
    onDropField,
    onContainerDrop,
    onShowColumn,
    onHideColumn,
    onShowAll,
    onHideAllUnpinned,
    onTogglePinned,
  } = useTableEditorDragDropController({
    config,
    update,
    hiddenColumns,
    visibleColumns,
  });

  const hasMoreHiddenColsThanCanAllBeAdded =
    config.columnOrder.length + hiddenColumns.length > COLUMN_LIMIT;

  const hiddenControls = (
    <Button
      onPress={onShowAll}
      variant="ghost"
      size="sm"
      endIcon="chevron-next"
      isDisabled={
        hiddenColumns.length === 0 || hasMoreHiddenColsThanCanAllBeAdded
      }>
      Show all
    </Button>
  );
  const visibleControls = (
    <Button
      onPress={onHideAllUnpinned}
      variant="ghost"
      size="sm"
      startIcon="chevron-back"
      isDisabled={visibleColumns.length === 0}>
      Hide unpinned
    </Button>
  );

  return (
    <div className="flex gap-16">
      <TableEditorColumn
        title="Hidden"
        titleControls={hiddenControls}
        count={hiddenColumns.length}
        onDrop={onContainerDrop}>
        <TableEditorColumnSection onDrop={onContainerDrop}>
          {hiddenColumns.length === 0 ? (
            <p className="px-16 py-8 text-moon-500">No hidden columns</p>
          ) : null}
          {hiddenColumns.slice(0, MAX_ITEMS_PER_SECTION).map(column => (
            <TableEditorColumnField
              hidden
              key={column}
              column={column}
              draggingColumnName={draggingColumnName}
              isDisabled={isColumnLimitReached}
              onDragStart={onDragStart}
              onDrop={onDropField}
              onMoveClick={onShowColumn}
              onPinClick={onTogglePinned}>
              {highlightColumn(column)}
            </TableEditorColumnField>
          ))}
          {hiddenColumns.length > MAX_ITEMS_PER_SECTION && (
            <p className="px-10 py-6 text-moon-500">
              Showing first {MAX_ITEMS_PER_SECTION} items. Use search to narrow
              down results.
            </p>
          )}
        </TableEditorColumnSection>
      </TableEditorColumn>
      <TableEditorColumn
        title="Visible & Pinned"
        titleControls={visibleControls}
        count={
          fixedColumns.length + pinnedColumns.length + visibleColumns.length
        }
        onDrop={onContainerDrop}
        visible>
        <TableEditorColumnSection onDrop={onContainerDrop} pinned visible>
          {fixedColumns.map(column => (
            <TableEditorColumnField key={column} column={column} fixed pinned>
              {highlightColumn(column)}
            </TableEditorColumnField>
          ))}
          {pinnedColumns.map(column => (
            <TableEditorColumnField
              key={column}
              column={column}
              draggingColumnName={draggingColumnName}
              pinned
              onDragStart={onDragStart}
              onDrop={onDropField}
              onMoveClick={onHideColumn}
              onPinClick={onTogglePinned}>
              {highlightColumn(column)}
            </TableEditorColumnField>
          ))}
        </TableEditorColumnSection>
        <TableEditorColumnSection onDrop={onContainerDrop} visible>
          {visibleColumns.length === 0 ? (
            <p className="px-16 py-8 text-moon-500">No visible columns</p>
          ) : null}
          {visibleColumns.slice(0, MAX_ITEMS_PER_SECTION).map(column => (
            <TableEditorColumnField
              key={column}
              column={column}
              draggingColumnName={draggingColumnName}
              onDragStart={onDragStart}
              onDrop={onDropField}
              onMoveClick={onHideColumn}
              onPinClick={onTogglePinned}>
              {highlightColumn(column)}
            </TableEditorColumnField>
          ))}
          {visibleColumns.length > MAX_ITEMS_PER_SECTION && (
            <p className="px-10 py-6 text-moon-500">
              Showing first {MAX_ITEMS_PER_SECTION} items. Use search to narrow
              down results.
            </p>
          )}
        </TableEditorColumnSection>
      </TableEditorColumn>
    </div>
  );
}
